import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useMutation } from '@tanstack/react-query';
import { useWizardStore, type BillingPeriod, type WizardCurrency } from '../wizardStore';
import { subscriptionApi } from '@/entities/subscription/api';
import { PrimaryButton } from '@/shared/ui/PrimaryButton';
import { WizardFooter } from '@/shared/ui/WizardFooter';
import { GlassSelect } from '@/shared/ui/GlassSelect';
import { NumberInput } from '@/shared/ui/NumberInput';
import { CalendarPicker } from '@/shared/ui/CalendarPicker';
import { useT } from '@/shared/lib/i18n';
import { useI18nStore } from '@/shared/lib/i18n';
import { cn } from '@/shared/lib/cn';

const CURRENCIES: { value: WizardCurrency; label: string }[] = [
  { value: 'RUB', label: '₽' },
  { value: 'USD', label: '$' },
  { value: 'EUR', label: '€' },
];

function formatDate(iso: string, lang: 'ru' | 'en'): string {
  return new Date(iso + 'T00:00:00').toLocaleDateString(lang === 'ru' ? 'ru' : 'en', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function buildPeriod(period: BillingPeriod, interval: number, unit: string) {
  if (period === 'monthly') return { interval: 1, unit: 'months' };
  if (period === 'quarterly') return { interval: 3, unit: 'months' };
  if (period === 'yearly') return { interval: 12, unit: 'months' };
  return { interval, unit };
}

export function PriceStep() {
  const {
    totalPrice, currency, period, customInterval, customUnit,
    billingDate, deadlineMode, deadlineDays, deadlineDirection,
    subscriptionId, update, next,
  } = useWizardStore();
  const t = useT();
  const lang = useI18nStore((s) => s.lang);
  const [showCalendar, setShowCalendar] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  const canContinue = parseFloat(totalPrice) > 0;

  const deadlineShift = deadlineMode === 'same' ? 0
    : deadlineMode === 'before' ? -1
    : deadlineDirection === 'before' ? -deadlineDays : deadlineDays;

  const patchMutation = useMutation({
    mutationFn: () => {
      if (!subscriptionId) { next(); return Promise.resolve(null); }
      return subscriptionApi.patch(subscriptionId, {
        total_price: parseFloat(totalPrice) || 0,
        currency,
        period: buildPeriod(period, customInterval, customUnit),
        billing_date: billingDate,
        deadline_shift: deadlineShift,
        draft_step: 'members',
      });
    },
    onSuccess: (result) => {
      if (result !== null) next();
    },
  });

  const PERIODS: { value: BillingPeriod; label: string }[] = [
    { value: 'monthly', label: t('wizard.price.monthLabel') },
    { value: 'quarterly', label: t('wizard.price.quarterLabel') },
    { value: 'yearly', label: t('wizard.price.yearLabel') },
    { value: 'custom', label: t('wizard.price.customLabel') },
  ];

  const UNITS: { value: 'days' | 'weeks' | 'months'; label: string }[] = [
    { value: 'days', label: t('wizard.price.days') },
    { value: 'weeks', label: t('wizard.price.weeks') },
    { value: 'months', label: t('wizard.price.months') },
  ];

  return (
    <div className="flex flex-col gap-4 flex-1">
      <h1 className="text-[24px] font-bold tracking-tight">{t('wizard.price.title')}</h1>

      {/* Price + currency */}
      <div className="flex gap-3">
        <div className="flex-[2]">
          <p className="text-[13px] text-text-secondary mb-1.5">{t('wizard.price.totalPrice')}</p>
          <input
            type="text"
            inputMode="decimal"
            placeholder="2 250"
            value={totalPrice}
            onChange={(e) => update({ totalPrice: e.target.value.replace(/[^0-9.]/g, '') })}
            className="w-full rounded-[14px] px-[13px] py-[13px] text-[20px] font-semibold bg-white/55 border border-white/60 outline-none focus:border-brand-green transition-colors"
          />
        </div>
        <div className="flex-1">
          <p className="text-[13px] text-text-secondary mb-1.5">{t('wizard.price.currency')}</p>
          <GlassSelect
            value={currency}
            options={CURRENCIES}
            onChange={(v) => update({ currency: v })}
          />
        </div>
      </div>

      {/* Period — sentence form */}
      <div>
        <p className="text-[13px] text-text-secondary mb-1.5">{t('wizard.price.period')}</p>
        <div className="flex items-center gap-2">
          <span className="text-[15px] font-medium text-text-primary whitespace-nowrap">
            {t('wizard.price.periodSentence')}
          </span>
          <GlassSelect
            value={period}
            options={PERIODS}
            onChange={(v) => update({ period: v })}
            className="flex-1"
          />
        </div>

        {period === 'custom' && (
          <div className="flex items-center gap-2 mt-2">
            <span className="text-[14px] text-text-secondary">{t('wizard.price.every')}</span>
            <NumberInput
              value={customInterval}
              onChange={(v) => update({ customInterval: v })}
              min={1}
              integer
              className="w-[64px] px-3 py-[9px] text-[18px] font-semibold text-center"
            />
            <GlassSelect
              value={customUnit}
              options={UNITS}
              onChange={(v) => update({ customUnit: v })}
              className="flex-1"
            />
          </div>
        )}
      </div>

      {/* Billing date */}
      <div>
        <div className="flex items-center gap-1.5 mb-1.5">
          <p className="text-[13px] text-text-secondary">{t('wizard.price.chargeDate')}</p>
          <button
            type="button"
            onClick={() => setShowTooltip((v) => !v)}
            className={cn(
              'w-[17px] h-[17px] rounded-full border flex items-center justify-center text-[10px] font-bold transition-colors flex-shrink-0',
              showTooltip
                ? 'border-brand-teal text-brand-teal bg-brand-teal/10'
                : 'border-text-muted/50 text-text-muted hover:border-text-secondary hover:text-text-secondary',
            )}
          >
            ?
          </button>
        </div>

        <AnimatePresence initial={false}>
          {showTooltip && (
            <motion.div
              key="tooltip"
              initial={{ opacity: 0, height: 0, marginBottom: 0 }}
              animate={{ opacity: 1, height: 'auto', marginBottom: 8 }}
              exit={{ opacity: 0, height: 0, marginBottom: 0 }}
              transition={{ duration: 0.18 }}
              className="overflow-hidden"
            >
              <p className="text-[12px] text-text-secondary bg-white/50 rounded-[10px] px-3 py-[10px] border border-white/60 leading-relaxed">
                {t('wizard.price.chargeDateTooltip')}
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        <button
          type="button"
          onClick={() => setShowCalendar(true)}
          className="w-full flex items-center justify-between rounded-[14px] px-[13px] py-[13px] bg-white/55 border border-white/60 hover:border-brand-green/40 transition-colors"
        >
          <span className="text-[16px] font-semibold text-text-primary">
            {formatDate(billingDate, lang)}
          </span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-text-muted flex-shrink-0">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
        </button>
      </div>

      <div className="flex-1" />

      <WizardFooter>
        {patchMutation.isError && (
          <p className="text-[13px] text-status-debt text-center">{t('wizard.preview.error')}</p>
        )}
        <PrimaryButton
          onClick={() => patchMutation.mutate()}
          disabled={!canContinue}
          loading={patchMutation.isPending}
        >
          {t('wizard.continue')}
        </PrimaryButton>
      </WizardFooter>

      {/* Calendar bottom sheet */}
      <AnimatePresence>
        {showCalendar && (
          <CalendarPicker
            value={billingDate}
            title={t('wizard.price.calTitle')}
            onChange={(v) => update({ billingDate: v })}
            onClose={() => setShowCalendar(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
